import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const AUDIENCE_OPTIONS = ["Owner", "Tenant", "Executive", "All"];

const emptyOption = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  text: ""
});

function isApiSuccess(data) {
  return data?.success === true || data?.status === "success";
}

// Convert an ISO/date string to the "datetime-local" input format (YYYY-MM-DDTHH:mm)
function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function pickPoll(data) {
  if (!data || typeof data !== "object") return null;
  if (data.poll && typeof data.poll === "object") return data.poll;
  if (data.eVoting && typeof data.eVoting === "object") return data.eVoting;
  if (data.result && typeof data.result === "object" && !Array.isArray(data.result)) return data.result;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    if (data.data.poll) return data.data.poll;
    return data.data;
  }
  if (data._id || data.id || data.question) return data;
  return null;
}

function getOptionText(opt) {
  if (typeof opt === "string") return opt;
  return String(opt?.text || opt?.option || opt?.label || opt?.name || "").trim();
}

function EVotingEdit() {
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const eVotingId = useMemo(() => {
    try { return idParam ? decodeURIComponent(String(idParam)).trim() : ""; }
    catch { return String(idParam || "").trim(); }
  }, [idParam]);

  const userId = useMemo(
    () =>
      localStorage.getItem("userid") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("id") ||
      "",
    []
  );

  const role = useMemo(
    () =>
      String(localStorage.getItem("role") || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, ""),
    []
  );
  const isTenant = role === "TENANT";

  // ── Form state ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState([emptyOption(), emptyOption()]);
  const [audience, setAudience] = useState("All");
  const [allowMultipleAnswers, setAllowMultipleAnswers] = useState(false);
  const [setStartTime, setSetStartTime] = useState(false);
  const [startTimeLocal, setStartTimeLocal] = useState("");
  const [setEndTime, setSetEndTime] = useState(false);
  const [endTimeLocal, setEndTimeLocal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  // Redirect tenants immediately
  useEffect(() => {
    if (isTenant) {
      toast.error("Tenants are not allowed to edit polls");
      navigate("/e-voting", { replace: true });
    }
  }, [isTenant, navigate]);

  // ── Load existing poll ────────────────────────────────────────────────────
  const fetchPoll = useCallback(async () => {
    if (!API_BASE_URL || !eVotingId || !userId) {
      setLoading(false);
      return;
    }
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(API_BASE_URL, {
        action: "eVoting.getById",
        userid: userId,
        eVotingId
      });
      const data = response?.data;
      if (!isApiSuccess(data)) {
        toast.error(data?.message || data?.error || "Failed to load poll");
        navigate("/e-voting", { replace: true });
        return;
      }
      const poll = pickPoll(data);
      if (!poll) {
        toast.error("Poll not found");
        navigate("/e-voting", { replace: true });
        return;
      }

      // Guard: only scheduled polls can be edited
      const pollStatus = String(poll.status || "").toLowerCase();
      if (pollStatus !== "scheduled") {
        toast.error("Only scheduled polls can be edited");
        navigate(`/e-voting-view/${eVotingId}`, { replace: true });
        return;
      }

      // Pre-fill form from existing poll data
      setQuestion(poll.question || "");

      const rawOptions = Array.isArray(poll.options) ? poll.options : [];
      const filled = rawOptions
        .map((opt) => getOptionText(opt))
        .filter(Boolean)
        .map((text) => ({ id: emptyOption().id, text }));
      setOptions(filled.length >= 2 ? filled : [emptyOption(), emptyOption()]);

      setAudience(
        AUDIENCE_OPTIONS.includes(poll.audience) ? poll.audience : "All"
      );
      setAllowMultipleAnswers(Boolean(poll.allowMultipleAnswers));

      if (poll.setStartTime && poll.startTime) {
        setSetStartTime(true);
        setStartTimeLocal(toLocalInputValue(poll.startTime));
      }
      if (poll.setEndTime && poll.endTime) {
        setSetEndTime(true);
        setEndTimeLocal(toLocalInputValue(poll.endTime));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to load poll");
      navigate("/e-voting", { replace: true });
    } finally {
      if (didIncrement) loaderDecrement();
      setLoading(false);
    }
  }, [API_BASE_URL, eVotingId, userId, navigate]);

  useEffect(() => { fetchPoll(); }, [fetchPoll]);

  // ── Option helpers ────────────────────────────────────────────────────────
  const updateOption = (id, text) =>
    setOptions((prev) => prev.map((row) => (row.id === id ? { ...row, text } : row)));

  const addOption = () => setOptions((prev) => [...prev, emptyOption()]);

  const removeOption = (id) =>
    setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((row) => row.id !== id)));

  const onDragStart = (index) => setDragIndex(index);
  const onDragOver = (event, index) => {
    event.preventDefault();
    if (dragIndex == null || dragIndex === index) return;
    setOptions((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(index);
  };
  const onDragEnd = () => setDragIndex(null);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!API_BASE_URL) { toast.error("Missing REACT_APP_API_URL in environment"); return; }
    if (!userId) { toast.error("Missing user id. Please login again."); return; }

    const trimmedQuestion = question.trim();
    const cleanedOptions = options
      .map((row) => String(row.text || "").trim())
      .filter(Boolean);

    if (!trimmedQuestion) { toast.error("Question is required"); return; }
    if (cleanedOptions.length < 2) { toast.error("Add at least 2 non-empty options"); return; }
    if (!AUDIENCE_OPTIONS.includes(audience)) { toast.error("Choose an audience"); return; }
    if (setEndTime && !endTimeLocal) { toast.error("End time is required when Set end time is on"); return; }
    if (setStartTime && !startTimeLocal) { toast.error("Start time is required when Set start time is on"); return; }
    if (setStartTime && startTimeLocal && new Date(startTimeLocal) <= new Date()) {
      toast.error("Start time must be in the future");
      return;
    }
    if (setStartTime && setEndTime && startTimeLocal && endTimeLocal) {
      if (new Date(startTimeLocal) >= new Date(endTimeLocal)) {
        toast.error("Start time must be before end time");
        return;
      }
    }

    const payload = {
      action: "eVoting.update",
      userid: userId,
      eVotingId,
      question: trimmedQuestion,
      options: cleanedOptions,
      audience,
      allowMultipleAnswers: Boolean(allowMultipleAnswers),
      setStartTime: Boolean(setStartTime),
      setEndTime: Boolean(setEndTime)
    };
    if (setStartTime && startTimeLocal) {
      payload.startTime = new Date(startTimeLocal).toISOString();
    }
    if (setEndTime && endTimeLocal) {
      payload.endTime = new Date(endTimeLocal).toISOString();
    }

    setSubmitting(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(API_BASE_URL, payload);
      const data = response?.data;
      if (!isApiSuccess(data)) {
        toast.error(data?.message || data?.error || "Failed to update poll");
        return;
      }
      toast.success(data?.message || "Poll updated successfully");
      navigate(`/e-voting-view/${eVotingId}`);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update poll");
    } finally {
      if (didIncrement) loaderDecrement();
      setSubmitting(false);
    }
  };

  if (isTenant) return null;

  if (loading) {
    return (
      <div className="employee-form">
        <div className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body text-muted">Loading poll…</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <h1 className="m-0 txt_lft">Edit poll</h1>
            <p style={{ margin: 0 }}>
              Only scheduled polls can be edited. Changes will re-register the EventBridge schedules automatically.
            </p>
          </div>
          <NavLink to={`/e-voting-view/${eVotingId}`} className="btn btn-secondary">
            Back
          </NavLink>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title mb-0">Poll details</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Question + Audience */}
                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label className="form-label" htmlFor="evoting-edit-question">
                        Question <span className="required-star">*</span>
                      </label>
                      <input
                        id="evoting-edit-question"
                        type="text"
                        className="form-control"
                        placeholder="Ask question"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        maxLength={300}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label" htmlFor="evoting-edit-audience">
                        Audience <span className="required-star">*</span>
                      </label>
                      <select
                        id="evoting-edit-audience"
                        className="form-control"
                        value={audience}
                        onChange={(e) => setAudience(e.target.value)}
                        required
                      >
                        {AUDIENCE_OPTIONS.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                      <small className="form-text text-muted">
                        Owner, Tenant, Executive, or All
                      </small>
                    </div>
                  </div>
                </div>

                {/* Options */}
                <div className="form-group">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">
                      Options <span className="required-star">*</span>
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={addOption}
                    >
                      <i className="fas fa-plus mr-1" aria-hidden />
                      Add option
                    </button>
                  </div>
                  {options.map((row, index) => (
                    <div
                      key={row.id}
                      className="d-flex align-items-center mb-2"
                      style={{ gap: 8, opacity: dragIndex === index ? 0.7 : 1 }}
                      draggable
                      onDragStart={() => onDragStart(index)}
                      onDragOver={(e) => onDragOver(e, index)}
                      onDragEnd={onDragEnd}
                    >
                      <span
                        className="text-muted"
                        title="Drag to reorder"
                        style={{ cursor: "grab", minWidth: 18 }}
                      >
                        <i className="fas fa-bars" aria-hidden />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Option ${index + 1}`}
                        value={row.text}
                        onChange={(e) => updateOption(row.id, e.target.value)}
                        maxLength={120}
                      />
                      {options.length > 2 ? (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          aria-label="Remove option"
                          onClick={() => removeOption(row.id)}
                        >
                          <i className="fas fa-trash" aria-hidden />
                        </button>
                      ) : null}
                    </div>
                  ))}
                  <small className="form-text text-muted">
                    At least 2 non-empty options are required.
                  </small>
                </div>

                {/* Toggles */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="evoting-edit-multiple"
                          checked={allowMultipleAnswers}
                          onChange={(e) => setAllowMultipleAnswers(e.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor="evoting-edit-multiple">
                          Allow multiple answers
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="evoting-edit-start-toggle"
                          checked={setStartTime}
                          onChange={(e) => {
                            setSetStartTime(e.target.checked);
                            if (!e.target.checked) setStartTimeLocal("");
                          }}
                        />
                        <label className="custom-control-label" htmlFor="evoting-edit-start-toggle">
                          Set start time
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="evoting-edit-end-toggle"
                          checked={setEndTime}
                          onChange={(e) => {
                            setSetEndTime(e.target.checked);
                            if (!e.target.checked) setEndTimeLocal("");
                          }}
                        />
                        <label className="custom-control-label" htmlFor="evoting-edit-end-toggle">
                          Set end time
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Time pickers */}
                {(setStartTime || setEndTime) ? (
                  <div className="row">
                    {setStartTime ? (
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="evoting-edit-start-time">
                            Start time <span className="required-star">*</span>
                          </label>
                          <input
                            id="evoting-edit-start-time"
                            type="datetime-local"
                            className="form-control"
                            value={startTimeLocal}
                            onChange={(e) => setStartTimeLocal(e.target.value)}
                            required={setStartTime}
                          />
                          <small className="form-text text-muted">
                            Must be in the future. EventBridge schedule will be updated.
                          </small>
                        </div>
                      </div>
                    ) : null}
                    {setEndTime ? (
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="evoting-edit-end-time">
                            End time <span className="required-star">*</span>
                          </label>
                          <input
                            id="evoting-edit-end-time"
                            type="datetime-local"
                            className="form-control"
                            value={endTimeLocal}
                            onChange={(e) => setEndTimeLocal(e.target.value)}
                            required={setEndTime}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {/* Actions */}
                <div
                  className="d-flex"
                  style={{ gap: 12, justifyContent: "flex-end", marginTop: 12 }}
                >
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? "Saving…" : "Save changes"}
                  </button>
                  <NavLink
                    to={`/e-voting-view/${eVotingId}`}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </NavLink>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EVotingEdit;
